import { CloseRoundLight, DoneLight, SettingLineLight } from '@repo/go-icons';
import { ActionsBar, Button, ConfigContext, Switch, Tooltip } from '@repo/go-ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { TimezoneSwitch } from '../../shared/components/TimezoneSwitch/TimezoneSwitch';
import type { AlertsManagerWidgetState } from '../AlertsManagerWidgetState';

import type { AutoAnomaliesService } from './AutoAnomaliesService';
import type { AutoAnomaliesTableStore } from './AutoAnomaliesTableStore';

export const AutoAnomaliesViewActionsBar = observer(function AutoAnomaliesViewActionsBar({
  widgetState,
  store,
  service,
}: {
  widgetState: AlertsManagerWidgetState;
  store: AutoAnomaliesTableStore;
  service: AutoAnomaliesService;
}) {
  const configProvider = useContext(ConfigContext);
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const save = () => {
    flowResult(service.save()).then(() => {
      service.setEditing(false);
      widgetState.eventHandlers.onEditModeChanged?.(false);
    });
  };

  const cancel = () => {
    widgetState.exitEditModeAsync(configProvider, i18n).catch((error: unknown) => {
      console.error(error);
    });
  };

  return (
    <ActionsBar>
      <ActionsBar.TopBar>
        {service.uiState.editing && (
          <>
            <ActionsBar.Item>
              <Tooltip placement="left" title={t('alertsManagerWidget:actionsBar.save')} delay={400}>
                <div>
                  <Button icon={<DoneLight />} variant="success" onClick={() => save()} />
                </div>
              </Tooltip>
            </ActionsBar.Item>
            <ActionsBar.Item>
              <Tooltip placement="left" title={t('alertsManagerWidget:actionsBar.cancelChanges')} delay={400}>
                <div>
                  <Button icon={<CloseRoundLight />} variant="danger" onClick={() => cancel()} />
                </div>
              </Tooltip>
            </ActionsBar.Item>
          </>
        )}
      </ActionsBar.TopBar>
      <ActionsBar.BottomBar>
        <Tooltip placement="left" title={t('alertsManagerWidget:tableSettings')} delay={400}>
          <ActionsBar.Item active={store.isTableSettingsOpen} onClick={() => store.setIsTableSettingsOpen(true)}>
            <SettingLineLight />
          </ActionsBar.Item>
        </Tooltip>
        <TimezoneSwitch timeZones={widgetState.timeZonesStore} />
        <Switch
          style={{ marginLeft: 4 }}
          orientation="vertical"
          size="m"
          checked={widgetState.settingsStore.scaleDepthUnit === 'ft'}
          leftNode={'m'}
          rightNode={'ft'}
          onChange={() =>
            widgetState.settingsStore.setScaleDepthUnit(widgetState.settingsStore.scaleDepthUnit === 'm' ? 'ft' : 'm')
          }
        />
      </ActionsBar.BottomBar>
    </ActionsBar>
  );
});
